import { Injectable, computed, signal } from '@angular/core';
import { makeKeyValueRepository } from 'src/data/repositories/key-value/key-value-repository-factory';
import { makeConfigService } from 'src/data/services/common/config/config-service-factory';
import { makeLogService } from 'src/data/services/common/log/log/log-service-factory';
import { RepositoryKey } from 'src/models/common/repository/repository-key';
import { Entity } from 'src/models/entity/entity';
import { makeLoadEntityFromCentralUseCase } from 'src/usecases/central/entity/load-entity-from-central/load-entity-from-central-use-case-factory';
import { makeSelectEntityUseCase } from 'src/usecases/entity/select-entity/select-entity-use-case-factory';

export type EntitySelectOption = {
  id: string;
  label: string;
  depth: number;
  guides: number[];
  entity: Entity;
};

@Injectable({
  providedIn: 'root',
})
export class EntitySelectionService {
  private logService = makeLogService();
  private keyValueRepository = makeKeyValueRepository();
  private configService = makeConfigService();
  private loadEntitiesUseCase = makeLoadEntityFromCentralUseCase();
  private selectEntityUseCase = makeSelectEntityUseCase();
  private initializePromise: Promise<void> | null = null;

  readonly entities = signal<Entity[]>([]);
  readonly entityOptions = signal<EntitySelectOption[]>([]);
  readonly entitiesLoading = signal(false);
  readonly displayRequiredDialog = signal(false);
  readonly pendingEntityId = signal('');
  readonly selectedEntityId = signal('');
  readonly selectedEntity = signal<Entity>({
    name: 'Entidades',
    id: '',
  } as Entity);
  readonly ready = signal(false);

  readonly hasEntities = computed(() => this.entityOptions().length > 0);
  readonly canConfirmEntitySelection = computed(() => !!this.pendingEntityId());
  readonly entityPlaceholder = computed(() =>
    this.entitiesLoading() ? 'Carregando entidades...' : 'Entidade',
  );
  readonly selectedEntityLabel = computed(
    () => this.selectedEntity()?.name || 'Entidade',
  );
  readonly entityAriaLabel = computed(
    () => `Entidade selecionada: ${this.selectedEntityLabel()}`,
  );

  initialize(): Promise<void> {
    if (this.initializePromise) {
      return this.initializePromise;
    }

    this.initializePromise = this.loadEntities();
    return this.initializePromise;
  }

  setPendingEntityId(entityId: string): void {
    this.pendingEntityId.set(entityId ?? '');
  }

  async selectEntityById(entityId: string): Promise<void> {
    if (!entityId || entityId === this.selectedEntityId()) {
      return;
    }

    const shouldReloadView = this.ready();
    const selectedOption = this.entityOptions().find(
      (option) => option.id === entityId,
    );
    if (!selectedOption) {
      return;
    }

    await this.applySelectedEntity(selectedOption.entity);
    await this.reloadCurrentRoute(shouldReloadView);
  }

  async confirmPendingEntitySelection(): Promise<void> {
    const entityId = this.pendingEntityId();
    if (!entityId) {
      return;
    }

    const shouldReloadView = this.ready();
    const selectedOption = this.entityOptions().find(
      (option) => option.id === entityId,
    );
    if (!selectedOption) {
      return;
    }

    await this.applySelectedEntity(selectedOption.entity);
    this.displayRequiredDialog.set(false);
    this.ready.set(true);
    await this.reloadCurrentRoute(shouldReloadView);
  }

  private async loadEntities(): Promise<void> {
    this.entitiesLoading.set(true);

    try {
      const entities = await this.loadEntitiesUseCase.handle();
      const entityOptions = this.parseEntitiesToOptions(entities);

      this.entities.set(entities);
      this.entityOptions.set(entityOptions);

      if (entityOptions.length === 0) {
        this.resetSelectionState();
        this.ready.set(true);
        return;
      }

      if (entityOptions.length === 1) {
        await this.applySelectedEntity(entityOptions[0].entity);
        this.displayRequiredDialog.set(false);
        this.ready.set(true);
        return;
      }

      const savedEntity = await this.keyValueRepository.get(
        RepositoryKey.SELECTED_ENTITY,
      );
      const selectedOption = entityOptions.find(
        (option) => option.id === savedEntity?.id,
      );

      if (!selectedOption) {
        this.resetSelectionState();
        this.displayRequiredDialog.set(true);
        this.ready.set(false);
        return;
      }

      await this.applySelectedEntity(selectedOption.entity);
      this.displayRequiredDialog.set(false);
      this.ready.set(true);
    } catch (error) {
      await this.logService.error('EntitySelectionService.loadEntities -> ', error);
      this.resetSelectionState();
      this.ready.set(true);
    } finally {
      this.entitiesLoading.set(false);
    }
  }

  private async applySelectedEntity(entity: Entity): Promise<void> {
    const selectedEntity = await this.selectEntityUseCase.handle(entity);
    const selectedEntityId = selectedEntity.id ?? '';

    this.selectedEntity.set(selectedEntity);
    this.selectedEntityId.set(selectedEntityId);
    this.pendingEntityId.set(selectedEntityId);
    await this.configService.reload();
  }

  private async reloadCurrentRoute(shouldReloadView: boolean): Promise<void> {
    if (!shouldReloadView) {
      return;
    }

    window.location.reload();
  }

  private parseEntitiesToOptions(
    entities: Entity[],
    depth: number = 0,
  ): EntitySelectOption[] {
    return entities.flatMap((entity) => {
      return [
        {
          id: entity.id,
          label: entity.name,
          depth,
          guides: Array.from({ length: depth }),
          entity,
        },
        ...this.parseEntitiesToOptions(entity.children ?? [], depth + 1),
      ];
    });
  }

  private resetSelectionState(): void {
    this.selectedEntity.set({ name: 'Entidades', id: '' } as Entity);
    this.selectedEntityId.set('');
    this.pendingEntityId.set('');
    this.displayRequiredDialog.set(false);
  }
}
